import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles, ChevronRight } from "lucide-react";
import { formatCurrency } from "@/components/StatusBadge";

/**
 * Recent activity marquee — surfaces two feeds from the last 7 days:
 *   • payment receipts    (green)
 *   • BD incentive credits (emerald)
 * Admin & accounts see every record; BDs see only their own scoped by the
 * backend endpoint. Rotates through events every 8s, pauses on hover.
 */
export default function RecentActivityBanner() {
  const [events, setEvents] = useState([]);
  const [idx, setIdx] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/api/activity/recent");
      const list = Array.isArray(data?.events) ? data.events : [];
      setEvents(list);
      setIdx((i) => (list.length ? i % list.length : 0));
    } catch { /* silent — banner just won't render */ }
  };
  useEffect(() => {
    // Reset the "dismissed" flag once a day so users get a fresh reminder
    // if they close it, but don't get spammed on every page nav.
    const today = new Date().toISOString().slice(0, 10);
    try {
      const stored = window.localStorage.getItem("activity-banner:dismissed");
      if (stored === today) setDismissed(true);
    } catch { /* ignore */ }
    load();
    const t = setInterval(load, 90_000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (events.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % events.length), 8000);
    return () => clearInterval(t);
  }, [events.length]);

  const dismiss = () => {
    setDismissed(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      window.localStorage.setItem("activity-banner:dismissed", today);
    } catch { /* ignore */ }
  };

  const current = events[idx];
  const summary = useMemo(() => {
    if (!events.length) return null;
    const pay = events.filter((e) => e.type === "payment").length;
    const inc = events.filter((e) => e.type === "incentive").length;
    return { pay, inc };
  }, [events]);

  if (dismissed || !current) return null;

  const isIncentive = current.type === "incentive";
  const label = current.label || (isIncentive ? "BD Incentive" : "Payment Received");
  const amount = isIncentive
    ? formatCurrency(current.amount_inr, "INR")
    : formatCurrency(current.amount, current.currency);
  const detail = current.detail || [
    current.client_name,
    current.project_name,
    current.bd_name && `BD: ${current.bd_name}`,
  ].filter(Boolean).join(" · ");

  return (
    <div
      className="sticky top-0 z-30 border-b border-emerald-700/40 bg-emerald-600/95 text-white px-4 py-2 flex items-center gap-3 backdrop-blur-md"
      data-testid="recent-activity-banner"
    >
      <style>{`
        @keyframes ts-activity-marquee { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .ts-activity-marquee { display: inline-block; white-space: nowrap; will-change: transform; animation: ts-activity-marquee 28s linear infinite; }
        .ts-activity-wrap:hover .ts-activity-marquee { animation-play-state: paused; }
        .ts-activity-wrap { overflow: hidden; flex: 1; min-width: 0; }
      `}</style>
      <Sparkles className="w-4 h-4 shrink-0" />
      <b className="shrink-0 uppercase text-[11px] tracking-wider">Last 7 days</b>
      <div className="ts-activity-wrap" title="Hover to pause">
        <span
          className="ts-activity-marquee text-sm cursor-pointer"
          onClick={() => navigate(current.link || (isIncentive ? "/incentives" : "/payments"))}
          data-testid="recent-activity-marquee"
        >
          <span className="inline-flex items-center gap-1 mr-2 font-semibold">
            {label}
          </span>
          <span className="text-white/95">{amount}</span>
          {current.payment_date && (
            <span className="text-white/80 ml-2">· {current.payment_date.slice(0, 10)}</span>
          )}
          {detail && <span className="text-white/85 ml-2">· {detail}</span>}
        </span>
      </div>
      {events.length > 1 && (
        <span className="text-[10px] bg-white/20 rounded-full px-1.5 py-0.5 whitespace-nowrap shrink-0"
              data-testid="recent-activity-counter">
          {idx + 1}/{events.length}
        </span>
      )}
      {summary && (
        <span className="hidden md:inline text-[10px] bg-white/15 rounded px-1.5 py-0.5 whitespace-nowrap shrink-0"
              data-testid="recent-activity-summary"
              title="Payments · Incentives">
          {summary.pay}P · {summary.inc}I
        </span>
      )}
      <button
        type="button"
        onClick={() => navigate("/payments")}
        className="hidden sm:inline-flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 rounded-full px-2 py-0.5 whitespace-nowrap shrink-0"
        data-testid="recent-activity-view-all"
      >
        View all <ChevronRight className="w-3 h-3" />
      </button>
      <button
        type="button"
        onClick={dismiss}
        className="text-xs underline hover:opacity-90 whitespace-nowrap shrink-0"
        data-testid="recent-activity-dismiss"
        title="Hide until tomorrow"
      >
        Hide today
      </button>
    </div>
  );
}
